import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AD_KIND_BY_SERVICE_TYPE } from './MyAdsBulkPromotion.constants';
import * as FlowS from '@/components/Ad/Promotions/PromotionFlow.styles';
import * as ServiceCheckboxesS from '@/components/Ad/Promotions/ServiceCheckboxes.styles';
import * as PaymentMethodsS from '@/components/Payment/PaymentMethods.styles';
import type {
  BulkPromotionService,
  BulkPromotionServiceOption,
} from '@/features/Profile/sections/MyAds/api';
import { ModalDrawer } from '@/components/UI/ModalDrawer';
import { Button } from '@/components/UI/Button/Button';
import { PaymentAgreementText } from '@/components/Payment/PaymentAgreementText';
import { formatNumber } from '@/utils/helpers';
import { getPromotionsData } from '@/components/Ad/Promotions/PromotionFlow.helpers';

type Props = {
  selectedCount: number;
  selectedService: BulkPromotionService | null;
  selectedOption: BulkPromotionServiceOption | null;
  selectedOptionId: string | null;
  walletBalance?: number;
  isPaymentLoading: boolean;
  onClose: () => void;
  onSelectOption: (serviceId: string) => void;
  onPay: () => void;
};

function getPaymentTitle(
  selectedService: BulkPromotionService | null,
  t: TFunction
) {
  if (!selectedService) return '';

  const adKind = AD_KIND_BY_SERVICE_TYPE[selectedService.serviceType];
  const presentation = getPromotionsData({ t })[adKind];

  return {
    iconSrc: `/images/${presentation.iconName}.svg`,
    text: presentation.title,
  };
}

export function MyAdsBulkPromotionPaymentDrawer({
  selectedCount,
  selectedService,
  selectedOption,
  selectedOptionId,
  walletBalance,
  isPaymentLoading,
  onClose,
  onSelectOption,
  onPay,
}: Props) {
  const { t } = useTranslation();

  return (
    <ModalDrawer
      isOpen={Boolean(selectedService)}
      onClose={onClose}
      title={getPaymentTitle(selectedService, t)}
      subtitle={
        <FlowS.Subtitle>
          {t('pages.cabinet.bulk_promotion_apply_description', {
            count: selectedCount,
          })}
        </FlowS.Subtitle>
      }
      bottom={
        <Button
          appearance="secondary"
          isFullWidth
          borderRadius={14}
          isLoading={isPaymentLoading}
          disabled={!selectedOption || isPaymentLoading}
          onClick={onPay}
        >
          {t('common.pay')}
        </Button>
      }
    >
      <FlowS.Content>
        <FlowS.Box>
          <FlowS.Title>{t('pages.ad.term_of_service')}</FlowS.Title>
          <FlowS.ServiceCheckboxesContainer>
            {selectedService?.options.map(option => (
              <ServiceCheckboxesS.ServiceCheckbox
                key={option.serviceId}
                name={`bulk-promotion-${option.serviceId}`}
                value={selectedOptionId === option.serviceId}
                onChangeHook={() => onSelectOption(option.serviceId)}
                appearance="radio"
                size="sm"
                color="secondary"
                labelPosition="right"
                hasBorderBottom={false}
                label={
                  <FlowS.ServiceLabelText>
                    {option.name} / {formatNumber(option.amount, 2)} ₼
                  </FlowS.ServiceLabelText>
                }
              />
            ))}
          </FlowS.ServiceCheckboxesContainer>
        </FlowS.Box>

        <FlowS.Box>
          <PaymentMethodsS.DrawerItemTitle>
            {t('pages.ad.payment_method')}
          </PaymentMethodsS.DrawerItemTitle>
          <PaymentMethodsS.List>
            <PaymentMethodsS.ListItem
              name="bulk-promotion-wallet"
              value
              appearance="radio"
              size="sm"
              color="secondary"
              labelPosition="right"
              hasBorderBottom={false}
              label={t('pages.ad.private_account', {
                amount: formatNumber(walletBalance ?? 0, 2),
              })}
            />
          </PaymentMethodsS.List>
        </FlowS.Box>
        <PaymentAgreementText />
      </FlowS.Content>
    </ModalDrawer>
  );
}
